import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import HeartbeatPushTimeline from 'components/Deals/HeartbeatPushTimeline'

import {
  useDealHeartbeatPreview,
  type CompositionWarning,
  type DealHeartbeatPreview as DealHeartbeatPreviewType,
  type SubscriptionResolution,
  type SubscriptionResolutionStatus,
} from 'api/aws/heartbeat/useDealHeartbeatPreview'
import {
  isHeartbeatDevPushEnabled,
  useSubmitDealToHeartbeat,
  type SubmitDealResult,
} from 'api/aws/heartbeat/useSubmitDealToHeartbeat'
import { renderColorByString } from 'components/DataGrid/renderColorByString'

/**
 * Dry-run preview of what the Phase-2 Grobotic → Heartbeat submission
 * flow would push when this deal hits Deal Won. Backend assembles the
 * payload + also pulls the live Heartbeat account snapshot via the
 * read-only SSH tunnel (when the org is already linked) so the user can
 * see CURRENT state alongside WOULD-PUSH state.
 *
 * Layout: a compact header row (Account · Parent · Submitter), then
 * the bulk sections (team, flow A subscriptions or flow B changes),
 * then a live Heartbeat state panel where applicable.
 */

const formatCurrency = (v: number | null | undefined): string => {
  if (v == null || !Number.isFinite(v)) return '—'
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const formatDate = (v: string | null | undefined): string => v?.slice(0, 10) ?? '—'

const actionChip = (action: 'not_linked' | 'already_linked' | 'none' | undefined): JSX.Element | null => {
  if (!action || action === 'none') return null
  // not_linked = push will be skipped with org_not_linked. Account
  // creation from Grobotic is disabled, so this is a hard block — not
  // an "about to happen" green state. Show it as an error chip so the
  // user understands the deal can't push until the link is resolved.
  const label = action === 'not_linked' ? 'Not linked — push blocked' : 'Already linked'
  const color = action === 'not_linked' ? 'error' : 'default'
  return <Chip size="small" label={label} color={color} variant={action === 'already_linked' ? 'outlined' : 'filled'} sx={{ height: 20 }} />
}

const changeTypeChip = (type: 'Increase' | 'Decrease' | 'Cancel'): JSX.Element => {
  const color = type === 'Cancel' ? 'error' : type === 'Increase' ? 'success' : 'warning'
  return <Chip size="small" label={type} color={color} sx={{ height: 20 }} />
}

// ── Predecessor source labels ────────────────────────────────────────────────

const predecessorSourceLabel = (source: string): string => {
  switch (source) {
    case 'predecessorId':
      return 'Explicit link'
    case 'fallback-master-agreement':
      return 'Same MSA chain'
    case 'fallback-same-type':
      return 'Same deal type'
    case 'fallback-latest-active':
      return 'Latest active'
    default:
      return source
  }
}

const predecessorSourceTooltip = (source: string): string => {
  switch (source) {
    case 'predecessorId':
      return "Deal's predecessorId field pointed at this prior deal — the strongest signal."
    case 'fallback-master-agreement':
      return 'Most recent active deal that shares this deal\'s masterAgreementId (same agreement chain).'
    case 'fallback-same-type':
      return 'No agreement chain link found; matched the most recent active deal of the same type (e.g. MSA → MSA).'
    case 'fallback-latest-active':
      return 'Last-resort fallback — the most recent active deal in this deal\'s family. Worth verifying the diff manually.'
    default:
      return source
  }
}

// ── Dev push result panel ────────────────────────────────────────────────────

const PushResultPanel: React.FC<{ result: SubmitDealResult; onDismiss: () => void }> = ({
  result,
  onDismiss,
}) => {
  const severity = result.success ? 'success' : 'error'
  return (
    <Alert severity={severity} sx={{ mb: 2 }} onClose={onDismiss}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Pushed (Flow {result.flow}) — {result.success ? 'success' : 'failed'}
        </Typography>
        <Chip
          size="small"
          label={`${result.emittedSubmissions} submission${result.emittedSubmissions === 1 ? '' : 's'}`}
          sx={{ height: 20 }}
        />
        {result.skipReason && (
          <Chip size="small" color="warning" label={result.skipReason} sx={{ height: 20 }} />
        )}
      </Stack>
      {result.contractResult && (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {result.contractResult.success
            ? `Contract #${result.contractResult.contractId} created (${result.contractResult.subscriptionIds?.length ?? 0} subs)`
            : `Contract push failed: ${result.contractResult.error}`}
        </Typography>
      )}
      {result.results.length > 0 && (
        <Stack gap={0.25} sx={{ mt: 0.5 }}>
          {result.results.map((r, i) => (
            <Typography
              key={i}
              variant="body2"
              sx={{ fontFamily: 'monospace', fontSize: 12, color: r.success ? 'success.main' : 'error.main' }}
            >
              {r.success ? '✔' : '✖'}{' '}
              {r.submissionType ? `${r.submissionType} → #${r.submissionId}` : r.error ?? 'unknown error'}
            </Typography>
          ))}
        </Stack>
      )}
    </Alert>
  )
}

// ── Composition-only change warning panel ──────────────────────────────────

/**
 * Surfaces the case where a service line's net MRR didn't move between
 * deal versions but the underlying product composition changed —
 * Heartbeat's submission model can't express this so no submission
 * fires, but the live Heartbeat subscriptions absolutely need manual
 * reconciliation. Renders as a prominent warning Alert listing dropped /
 * added / repriced products per affected service line.
 */
const CompositionWarningPanel: React.FC<{ warnings: CompositionWarning[] }> = ({ warnings }) => (
  <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Silent reconciliation needed
      </Typography>
      <Chip
        size="small"
        label={`${warnings.length} service line${warnings.length === 1 ? '' : 's'}`}
        color="warning"
        sx={{ height: 20 }}
      />
    </Stack>
    <Typography variant="body2" sx={{ mb: 1.5, fontSize: 13 }}>
      Net MRR on the line(s) below is unchanged between versions, so Heartbeat's submission model
      doesn&apos;t emit a service-change submission. The underlying Heartbeat subscriptions still
      need manual reconciliation — listed below per line.
    </Typography>
    <Stack gap={1.5}>
      {warnings.map(w => (
        <Box
          key={w.service_line}
          sx={{
            border: '1px solid',
            borderColor: 'warning.main',
            borderRadius: 1,
            p: 1.25,
            bgcolor: theme => alpha(theme.palette.warning.main, 0.04),
          }}
        >
          <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
              {w.service_line}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              total MRR unchanged at {formatCurrency(w.unchanged_mrr)}
            </Typography>
          </Stack>
          {w.dropped.length > 0 && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main' }}>
                Dropped (cancel in Heartbeat):
              </Typography>
              <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
                {w.dropped.map((d, i) => (
                  <Chip
                    key={`drop-${i}`}
                    size="small"
                    label={`${d.product_name}  ·  ${formatCurrency(d.mrr)}/mo`}
                    color="error"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
          {w.added.length > 0 && (
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                Added (create in Heartbeat):
              </Typography>
              <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
                {w.added.map((a, i) => (
                  <Chip
                    key={`add-${i}`}
                    size="small"
                    label={`${a.product_name}  ·  ${formatCurrency(a.mrr)}/mo`}
                    color="success"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
          {w.repriced.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'info.main' }}>
                Repriced (adjust in Heartbeat):
              </Typography>
              <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
                {w.repriced.map((r, i) => (
                  <Chip
                    key={`rep-${i}`}
                    size="small"
                    label={`${r.product_name}  ·  ${formatCurrency(r.old_mrr)} → ${formatCurrency(r.new_mrr)}/mo`}
                    color="info"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      ))}
    </Stack>
  </Alert>
)

// ── Sandbox fallback chip ────────────────────────────────────────────────────

const sandboxFallbackLabel = (reason: string | undefined): string => {
  switch (reason) {
    case 'prod_name_mismatch':
      return 'id collision → sandbox'
    case 'prod_unavailable':
      return 'prod unavailable → sandbox'
    case 'prod_not_found':
    default:
      return 'from sandbox'
  }
}

const sandboxFallbackTooltip = (reason: string | undefined): string => {
  switch (reason) {
    case 'prod_name_mismatch':
      return "An account with this heartbeat_account_id exists in prod but its name doesn't match the Grobotic org — almost certainly a different company. Data was loaded from your local Sail sandbox instead. Dev only; will never fire in production."
    case 'prod_unavailable':
      return 'Prod tunnel was unreachable; data was loaded from your local Sail sandbox. Dev only.'
    case 'prod_not_found':
    default:
      return 'Prod tunnel had no record of this heartbeat_account_id; data was loaded from your local Sail sandbox. Dev only.'
  }
}

// ── Subscription overlay (live Heartbeat vs new deal) ────────────────────────

/** Visual treatment for each live subscription based on how it'll be
 *  affected by submitting this deal. Designed to be readable at a glance
 *  in a dense table — left-border accent + chip, no full-row colour wash
 *  (which would fight with the per-cell status colouring already there). */
const overlayStyle = (
  status: SubscriptionResolutionStatus,
): { label: string; borderColor: string; rowBg: string; chipBg: string; chipFg: string } => {
  switch (status) {
    case 'kept':
      return { label: 'Kept', borderColor: '#2e7d32', rowBg: 'rgba(46, 125, 50, 0.04)', chipBg: '#2e7d32', chipFg: '#fff' }
    case 'will_cancel':
      return { label: 'Will cancel', borderColor: '#c62828', rowBg: 'rgba(198, 40, 40, 0.06)', chipBg: '#c62828', chipFg: '#fff' }
    case 'other_chain':
      return { label: 'Other chain', borderColor: '#ed6c02', rowBg: 'rgba(237, 108, 2, 0.05)', chipBg: '#ed6c02', chipFg: '#fff' }
    case 'no_predecessor':
    default:
      return { label: 'Unaffected', borderColor: '#9e9e9e', rowBg: 'transparent', chipBg: '#e0e0e0', chipFg: '#424242' }
  }
}

const LEGEND_ITEMS: Array<{ status: SubscriptionResolutionStatus; tooltip: string }> = [
  { status: 'kept', tooltip: 'Represented in the new deal — will stay (possibly updated).' },
  { status: 'will_cancel', tooltip: 'On the predecessor deal but NOT in the new deal — submission will cancel this.' },
  { status: 'other_chain', tooltip: "Not on the new deal or the predecessor — likely from a sibling agreement chain (e.g. CSA next to MSA). Won't be touched." },
  { status: 'no_predecessor', tooltip: 'Flow A — no predecessor; existing subs are unaffected by this submission.' },
]

const SubscriptionOverlayLegend: React.FC = () => (
  <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
    {LEGEND_ITEMS.map(item => {
      const style = overlayStyle(item.status)
      return (
        <Tooltip key={item.status} title={item.tooltip}>
          <Chip
            size="small"
            label={style.label}
            sx={{
              bgcolor: style.chipBg,
              color: style.chipFg,
              fontSize: '0.7rem',
              fontWeight: 600,
              height: 20,
            }}
          />
        </Tooltip>
      )
    })}
  </Stack>
)

// ── Compact header card cell ─────────────────────────────────────────────────

const HeaderCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ flex: 1, minWidth: 180 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Box sx={{ mt: 0.25 }}>{children}</Box>
  </Box>
)

const CompactHeader: React.FC<{ preview: DealHeartbeatPreviewType }> = ({ preview }) => {
  const a = preview.account
  const p = preview.parent_account
  const s = preview.submitter
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Stack direction="row" gap={2} divider={<Divider orientation="vertical" flexItem />} flexWrap="wrap">
        {/* Account */}
        <HeaderCell label="Account">
          {a ? (
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body2" fontWeight={500} noWrap>
                {a.name}
              </Typography>
              {actionChip(a.action)}
              {a.heartbeat_account_id != null && (
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  id={a.heartbeat_account_id}
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
        </HeaderCell>

        {/* Parent */}
        <HeaderCell label="Parent">
          {p.action === 'none' ? (
            <Typography variant="body2" color="text.secondary">No parent</Typography>
          ) : (
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body2" fontWeight={500} noWrap>
                {p.name ?? '—'}
              </Typography>
              {actionChip(p.action)}
              {p.heartbeat_account_id != null && (
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  id={p.heartbeat_account_id}
                </Typography>
              )}
            </Stack>
          )}
        </HeaderCell>

        {/* Submitter */}
        <HeaderCell label="Submitter">
          {s.user_uuid ? (
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body2" fontWeight={500} noWrap>
                {s.name ?? s.email ?? '—'}
              </Typography>
              {s.heartbeat_user_id != null ? (
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  user_id={s.heartbeat_user_id}
                </Typography>
              ) : (
                <Tooltip title="Owner not mirrored yet — push lands with NULL user_id.">
                  <Chip size="small" label="Not mirrored" color="warning" variant="outlined" sx={{ height: 20 }} />
                </Tooltip>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">No owner on deal</Typography>
          )}
        </HeaderCell>
      </Stack>
    </Paper>
  )
}

// ── Component ────────────────────────────────────────────────────────────────

const DealHeartbeatPreview = (): JSX.Element => {
  const { PK, SK } = useParams<{ PK: string; SK: string }>()
  const { data, error, isLoading } = useDealHeartbeatPreview(PK, SK)

  // All hooks must run on every render — keep useMemo above any early
  // returns so the hook order is stable (Rules of Hooks). Empty data is
  // handled by the resolutions array defaulting to [].
  const resolutionsForMemo: SubscriptionResolution[] =
    data?.preview?.current_heartbeat_state?.available
      ? data.preview.current_heartbeat_state.subscription_resolutions
      : []
  const resolutionById = useMemo(() => {
    const m = new Map<number, SubscriptionResolution>()
    for (const r of resolutionsForMemo) m.set(r.subscription_id, r)
    return m
  }, [resolutionsForMemo])

  // Dev push hooks — must stay above the early returns below so the hook
  // order is stable across render passes (Rules of Hooks). The button
  // itself short-circuits when the preview hasn't loaded yet.
  const devPushEnabled = isHeartbeatDevPushEnabled()
  const submitMutation = useSubmitDealToHeartbeat()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [lastPushResult, setLastPushResult] = useState<SubmitDealResult | null>(null)
  const [lastPushError, setLastPushError] = useState<string | null>(null)

  // "Currently in Heartbeat (live, read-only)" is collapsed by default
  // so the preview/push-action takes priority above the fold. Live state
  // is reference info — open on demand.
  const [liveStateExpanded, setLiveStateExpanded] = useState(false)
  const handleConfirmPush = (): void => {
    if (!PK || !SK) return
    setLastPushError(null)
    submitMutation.mutate(
      { PK, SK, trigger: 'dev_button' },
      {
        onSuccess: resp => {
          setLastPushResult(resp.result)
          setConfirmOpen(false)
        },
        onError: err => {
          setLastPushError(err.response?.data?.error ?? err.message)
          setConfirmOpen(false)
        },
      },
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading Heartbeat preview…
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load Heartbeat preview: {error.response?.data?.error ?? error.message}
        </Alert>
      </Box>
    )
  }

  if (!data) return <Box />
  const p = data.preview

  if (p.skipReason === 'org_not_found' || p.skipReason === 'deal_not_found') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Couldn't build a preview: {p.skipReason.replace('_', ' ')}.</Alert>
      </Box>
    )
  }

  const currentHB = p.current_heartbeat_state
  const liveSnapshot = currentHB?.available ? currentHB.snapshot : null

  return (
    <Box
      sx={{
        p: 2,
        // pb leaves room for the fixed Deal Lineage footer (~80px) so the
        // last subscription row isn't hidden behind it when the internal
        // scroll reaches the bottom. Unconditional — harmless on
        // non-versioned deals (footer simply doesn't render).
        pb: 12,
        maxHeight: 'calc(100vh - 180px)',
        overflowY: 'auto',
      }}
    >
      {/* Flow banner */}
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
        <Typography variant="h6">Heartbeat Push Preview</Typography>
        <Chip
          size="small"
          label={
            p.flow === 'A'
              ? p.renewal_context
                ? 'Flow A · Renewal Contract'
                : 'Flow A · New Contract'
              : p.flow === 'B'
              ? 'Flow B · Service Change'
              : p.flow === 'IO'
              ? 'Flow IO · Insertion Order'
              : 'No flow'
          }
          color={
            p.flow === 'A'
              ? 'primary'
              : p.flow === 'B'
              ? 'secondary'
              : p.flow === 'IO'
              ? 'info'
              : 'default'
          }
        />
        <Typography variant="body2" color="text.secondary">
          {p.flow === 'A'
            ? p.renewal_context
              ? p.renewal_context.reason === 'no_hb_contract'
                ? `Renewal of v${p.renewal_context.predecessor.semanticVersion ?? '?'}, still live, but Heartbeat has no contract for this account. The push backfills v${p.renewal_context.predecessor.semanticVersion ?? '?'} as history, books this as the contract, and sends each MRR change against v${p.renewal_context.predecessor.semanticVersion ?? '?'} as a submission.`
                : `Renewal of v${p.renewal_context.predecessor.semanticVersion ?? '?'} — predecessor ended ${p.renewal_context.predecessor.endDate ?? '?'}, pushing as a fresh contract.`
              : 'No predecessor — would create a new contract + subscriptions.'
            : p.flow === 'B'
            ? `Predecessor via ${p.flow_b?.predecessor.source} — ${p.flow_b?.changes.length ?? 0} submission(s).`
            : p.flow === 'IO'
            ? `Temporary Increase per product — ${p.flow_io?.submissions.length ?? 0} submission(s), ongoing_request=No.`
            : ''}
        </Typography>

        {/* Dev-only: fire the same orchestrator the CLI uses. Gated by
            VITE_ENV !== 'production' so the button doesn't ship to prod. */}
        {devPushEnabled && p.flow !== 'none' && (() => {
          const orgUnlinked = p.account?.action === 'not_linked'
          const badPricing = (p.pricing_problems?.length ?? 0) > 0
          return (
            <Box sx={{ ml: 'auto' }}>
              <Tooltip
                title={
                  orgUnlinked
                    ? 'Push blocked — organization is not linked to a Heartbeat account. Resolve the link first.'
                    : badPricing
                      ? 'Push blocked — a product is priced so it would send $0 of recurring revenue. Fix the margin on the deal first.'
                      : 'Dev only — POSTs this deal to Heartbeat (sandbox in dev). Same logic as the test-deal-submission-push CLI.'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={<RocketLaunchIcon />}
                    disabled={submitMutation.isPending || orgUnlinked || badPricing}
                    onClick={() => setConfirmOpen(true)}
                    sx={{ textTransform: 'none', fontSize: 12 }}
                  >
                    {submitMutation.isPending ? 'Pushing…' : 'Push to Heartbeat (dev)'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )
        })()}
      </Stack>

      {/* Org-not-linked blocker — shown when the Grobotic org has no
          heartbeat_account_id set. Account creation from Grobotic is
          disabled, so the push will skip with org_not_linked. Surfaced
          prominently here because the user can't fix it from the deal
          UI; they need to resolve the link in MOTHERSHIP first. */}
      {p.account?.action === 'not_linked' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{p.account.name}</strong> is not linked to a Heartbeat account. This deal
          cannot be pushed until the organization is connected (heartbeat_account_id must
          be set in MOTHERSHIP). Account creation from Grobotic is disabled — resolve the
          link via the match-active-orgs workflow, then re-open this preview.
        </Alert>
      )}

      {/* Pricing blocker — an ad-revenue product with a 0% margin prices its
          whole value as media spend, which is excluded from MRR, so the
          contract would book $0 of recurring revenue for a service billing
          thousands. The push refuses rather than sending that silently; the
          margin has to be corrected on the deal. */}
      {(p.pricing_problems?.length ?? 0) > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Push blocked — {p.pricing_problems.length} service line
            {p.pricing_problems.length === 1 ? '' : 's'} would send $0 of recurring revenue
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            {p.pricing_problems.map(problem => (
              <li key={problem.serviceLine}>
                <Typography variant="body2">{problem.detail}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {/* Push-result banner — renders after a successful or failed push */}
      {lastPushError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLastPushError(null)}>
          Push failed: {lastPushError}
        </Alert>
      )}
      {lastPushResult && (
        <PushResultPanel result={lastPushResult} onDismiss={() => setLastPushResult(null)} />
      )}

      {/* Confirmation dialog — explicit gesture before a write fires */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Push this deal to Heartbeat?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            This fires the same orchestrator as the CLI and writes to whichever Heartbeat the backend is
            configured for (sandbox in dev). Cancel and review the Heartbeat Preview tables behind this
            dialog if you haven't already.
          </DialogContentText>
          {p.flow === 'A' && (
            <DialogContentText sx={{ fontSize: 13, mt: 1.5, color: 'info.main' }}>
              {p.renewal_context?.reason === 'no_hb_contract'
                ? `Flow A (renewal of v${p.renewal_context.predecessor.semanticVersion ?? '?'}, no Heartbeat contract yet) — backfills the predecessor as history, creates the contract, and sends ${p.flow_a?.flighted_activations.length ?? 0} service-change submission(s).`
                : p.renewal_context
                ? `Flow A (renewal of v${p.renewal_context.predecessor.semanticVersion ?? '?'}) — creates a new contract + ${p.flow_a?.subscriptions.length ?? 0} subscription(s) for the new period. The predecessor's subs expire naturally.`
                : `Flow A — creates a new contract + ${p.flow_a?.subscriptions.length ?? 0} subscription(s).`}
            </DialogContentText>
          )}
          {p.flow === 'B' && (
            <DialogContentText sx={{ fontSize: 13, mt: 1.5, color: 'info.main' }}>
              Flow B — emits {p.flow_b?.changes.length ?? 0} service-change submission(s) vs.{' '}
              {p.flow_b?.predecessor.SK}.
            </DialogContentText>
          )}
          {p.flow === 'IO' && (
            <DialogContentText sx={{ fontSize: 13, mt: 1.5, color: 'info.main' }}>
              Flow IO — emits {p.flow_io?.submissions.length ?? 0} temporary Increase submission(s).
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPush}
            variant="contained"
            color="warning"
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending ? 'Pushing…' : 'Push'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compact header: Account · Parent · Submitter */}
      <CompactHeader preview={p} />

      {/* Team members — only render if there ARE any */}
      {p.account && p.account.team_members.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2">
              Team Members{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                ({p.account.team_members.filter(t => t.mirrored).length}/{p.account.team_members.length} mirrored)
              </Typography>
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { textAlign: 'left', py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>user_id</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p.account.team_members.map(m => (
                  <TableRow key={m.user_uuid} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: renderColorByString(m.role), fontWeight: 500 }}>
                        {m.role}
                      </Typography>
                    </TableCell>
                    <TableCell>{m.name ?? '—'}</TableCell>
                    <TableCell>{m.email ?? '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {m.heartbeat_user_id ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {m.mirrored ? (
                        <Chip size="small" label="Mirrored" color="success" variant="outlined" sx={{ height: 20 }} />
                      ) : (
                        <Chip size="small" label="Skip (not mirrored)" color="warning" variant="outlined" sx={{ height: 20 }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Flighted schedule banner — fires for either flow when the current
          deal has future-dated products. On Flow A, future products become
          service-change submissions at push time. On Flow B the banner is
          informational only; the service-line diff still emits a single
          aggregated submission per service line (flighted dates aren't
          currently used to split a service-line Increase across multiple
          effective_dates). */}
      {(() => {
        const flighted =
          p.flow === 'A' && p.flow_a?.is_flighted
            ? { kind: 'A' as const, activations: p.flow_a.flighted_activations }
            : p.flow === 'B' && p.flow_b?.is_flighted
              ? { kind: 'B' as const, activations: p.flow_b.flighted_activations }
              : null
        if (!flighted) return null
        return (
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                ⚑ Flighted Schedule
              </Typography>
              <Chip
                size="small"
                label={`${flighted.activations.length} scheduled change${flighted.activations.length === 1 ? '' : 's'}`}
                color="info"
                sx={{ height: 20 }}
              />
            </Stack>
            <Typography variant="body2" sx={{ mb: 1.5, fontSize: 13 }}>
              {flighted.kind === 'A' && p.renewal_context?.reason === 'no_hb_contract' ? (
                <>
                  Each row is a service-change submission sent with the contract, measured
                  against what v{p.renewal_context.predecessor.semanticVersion ?? '?'} bills the
                  day before this deal starts. The contract sends no subscription rows (NetSuite
                  owns those), so these submissions are how a change at the start date reaches
                  billing.
                </>
              ) : (
                <>This deal&apos;s MRR changes on dates LATER than the deal&apos;s start date.</>
              )}
              {flighted.kind === 'A' && p.renewal_context?.reason === 'no_hb_contract' ? null : flighted.kind === 'A' ? (
                <>
                  {' '}The contract below carries only the opening level. Each later step becomes a
                  service-change submission at push time, dated to when the MRR actually moves.
                  Current/New MRR are the service line totals either side of that date, so a
                  scheduled step down reports as a <em>Decrease</em>. Heartbeat keeps one submission
                  per deal + change type + service line, so multiple same-direction steps on one
                  line are merged into a single row and the full schedule is listed in its
                  description.
                </>
              ) : (
                <>
                  {' '}The version diff above already carries these dates: it runs on the same
                  MRR timeline, so the transition from the predecessor and each later step inside
                  this version each get their own dated submission. The table below is the same
                  schedule viewed per service line.
                </>
              )}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ '& .MuiTableCell-root': { textAlign: 'left', py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Service Line</TableCell>
                    <TableCell>Change</TableCell>
                    <TableCell>Effective</TableCell>
                    <TableCell>Length</TableCell>
                    <TableCell>Current MRR</TableCell>
                    <TableCell>New MRR</TableCell>
                    <TableCell>Products</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flighted.activations.map((a, i) => (
                    <TableRow key={`flight-${i}`} hover>
                      <TableCell>
                        <Chip
                          label={a.service_line}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={a.change_type}
                          size="small"
                          color={
                            a.change_type === 'Increase'
                              ? 'success'
                              : a.change_type === 'Decrease'
                                ? 'warning'
                                : 'error'
                          }
                          sx={{ height: 20 }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(a.effective_date)}</TableCell>
                      <TableCell>{a.length_of_change_months}mo</TableCell>
                      <TableCell>{formatCurrency(a.current_mrr)}</TableCell>
                      <TableCell>{formatCurrency(a.recurring_amount)}</TableCell>
                      <TableCell>
                        <Tooltip title={a.description}>
                          <Typography variant="body2">{a.product_name}</Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Alert>
        )
      })()}

      {/* Flow A — Contract + subscriptions */}
      {p.flow === 'A' && p.flow_a && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="subtitle2">
                Would Push (Contract{p.flow_a.is_flighted ? ' — active wave only' : ''})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {p.flow_a.subscriptions.length} subscription line(s) · start {p.flow_a.start_date} · end {p.flow_a.end_date ?? '—'}
              </Typography>
              <Stack direction="row" gap={0.5}>
                {p.flow_a.service_lines.map(sl => (
                  <Chip key={sl} label={sl} size="small" variant="outlined" sx={{ height: 20 }} />
                ))}
              </Stack>
            </Stack>
          </Box>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { textAlign: 'left', py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Service Line</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Portion</TableCell>
                  <TableCell>Recurring</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell>Flags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p.flow_a.subscriptions.map((s, i) => (
                  <TableRow key={`${s.name}-${i}`} hover>
                    <TableCell>{s.service_line_code}</TableCell>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={s.portion} variant="outlined" sx={{ height: 20 }} />
                    </TableCell>
                    <TableCell>{formatCurrency(s.recurring_amount)}</TableCell>
                    <TableCell>{formatCurrency(s.total)}</TableCell>
                    <TableCell>{formatDate(s.start_date)}</TableCell>
                    <TableCell>{formatDate(s.end_date)}</TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5}>
                        {s.is_mrr && <Chip size="small" label="MRR" color="success" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                        {s.is_service_mrr && <Chip size="small" label="Svc" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 600, borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell colSpan={3}>Totals</TableCell>
                  <TableCell>{formatCurrency(p.flow_a.totals.monthly)}</TableCell>
                  <TableCell>{formatCurrency(p.flow_a.totals.contract)}</TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Flow B — service changes */}
      {p.flow === 'B' && p.flow_b && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2">Would Push (Service Changes)</Typography>
              <Typography component="span" variant="caption" color="text.secondary">
                vs. <Box component="span" fontFamily="monospace">{p.flow_b.predecessor.SK}</Box>
                {p.flow_b.predecessor.name ? ` · ${p.flow_b.predecessor.name}` : ''}
                {p.flow_b.predecessor.type ? ` (${p.flow_b.predecessor.type})` : ''}
              </Typography>
              <Tooltip title={predecessorSourceTooltip(p.flow_b.predecessor.source)}>
                <Chip
                  size="small"
                  label={predecessorSourceLabel(p.flow_b.predecessor.source)}
                  color={p.flow_b.predecessor.source === 'predecessorId' ? 'success' : p.flow_b.predecessor.source === 'fallback-latest-active' ? 'warning' : 'info'}
                  variant="outlined"
                  sx={{ height: 20 }}
                />
              </Tooltip>
            </Stack>
          </Box>
          {p.flow_b.changes.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No per-service MRR changes detected. Nothing would be POSTed.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { textAlign: 'left', py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Change</TableCell>
                    <TableCell>Service Line</TableCell>
                    <TableCell>Effective</TableCell>
                    <TableCell>Current MRR</TableCell>
                    <TableCell>New MRR</TableCell>
                    <TableCell>Delta</TableCell>
                    <TableCell>Predecessor products</TableCell>
                    <TableCell>New products</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {p.flow_b.changes.map((c, i) => (
                    <TableRow key={`${c.service_line}-${c.effective_date}-${i}`} hover>
                      <TableCell>{changeTypeChip(c.change_type)}</TableCell>
                      <TableCell>{c.service_line}</TableCell>
                      <TableCell>
                        {formatDate(c.effective_date)}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {c.length_of_change_months}mo
                        </Typography>
                      </TableCell>
                      <TableCell>{formatCurrency(c.current_mrr)}</TableCell>
                      <TableCell>{formatCurrency(c.new_mrr)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: c.delta_mrr > 0 ? 'success.main' : c.delta_mrr < 0 ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
                          {c.delta_mrr > 0 ? '+' : ''}
                          {formatCurrency(c.delta_mrr)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {c.current_products.length === 0 ? <Typography variant="caption" color="text.secondary">—</Typography> :
                            c.current_products.map((pn, idx) => <Chip key={idx} label={pn} size="small" variant="outlined" sx={{ height: 20 }} />)}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                          {c.new_products.length === 0 ? <Typography variant="caption" color="text.secondary">—</Typography> :
                            c.new_products.map((pn, idx) => <Chip key={idx} label={pn} size="small" variant="outlined" sx={{ height: 20 }} />)}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Composition-only change warnings — Flow B service lines where
          net MRR didn't move but underlying products shifted. Heartbeat
          won't see a submission for these; ops needs to reconcile the
          subscriptions manually. */}
      {p.flow === 'B' &&
        p.flow_b &&
        p.flow_b.composition_warnings.length > 0 && (
          <CompositionWarningPanel warnings={p.flow_b.composition_warnings} />
        )}

      {/* Flow IO — Insertion Order: one Increase submission per product */}
      {p.flow === 'IO' && p.flow_io && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2">Would Push (Insertion Order)</Typography>
              <Typography component="span" variant="caption" color="text.secondary">
                {p.flow_io.deal_type ?? 'IO'} · {p.flow_io.submissions.length} temporary Increase submission(s)
              </Typography>
              <Chip size="small" label="Permanent: No" color="info" variant="outlined" sx={{ height: 20 }} />
              {p.flow_io.baseline_missing && (
                <Tooltip title="Couldn't fetch the live Heartbeat baseline — current_mrr defaults to 0 for each row. Verify before submitting.">
                  <Chip size="small" label="Baseline missing" color="warning" sx={{ height: 20 }} />
                </Tooltip>
              )}
            </Stack>
          </Box>
          {p.flow_io.submissions.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No IO products with a recurring amount &gt; 0. Nothing would be POSTed.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { textAlign: 'left', py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Service Line</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Current MRR</TableCell>
                    <TableCell>New MRR</TableCell>
                    <TableCell>Delta</TableCell>
                    <TableCell>Effective</TableCell>
                    <TableCell>Length</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {p.flow_io.submissions.map((s, i) => (
                    <TableRow key={`${s.service_line}-${i}`} hover>
                      <TableCell>{s.service_line}</TableCell>
                      <TableCell>
                        <Tooltip title={s.description}>
                          <Typography variant="body2">{s.product_name}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatCurrency(s.current_mrr)}</TableCell>
                      <TableCell>{formatCurrency(s.new_mrr)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                          +{formatCurrency(s.delta_mrr)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(s.effective_date)}</TableCell>
                      <TableCell>
                        {s.length_of_change_months}mo
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Live Heartbeat state via tunnel — only when account is linked + tunnel responded */}
      {currentHB && currentHB.available && liveSnapshot && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box
            onClick={() => setLiveStateExpanded(v => !v)}
            role="button"
            aria-expanded={liveStateExpanded}
            sx={{
              px: 2,
              py: 1,
              borderBottom: liveStateExpanded ? '1px solid' : 'none',
              borderColor: 'divider',
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1.5}
              flexWrap="wrap"
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transform: liveStateExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  color: 'text.secondary',
                }}
              />
              <Typography variant="subtitle2">Currently in Heartbeat (live, read-only)</Typography>
              {liveSnapshot.source === 'sandbox' && (
                <Tooltip title={sandboxFallbackTooltip(liveSnapshot.fallback_reason)}>
                  <Chip
                    size="small"
                    label={sandboxFallbackLabel(liveSnapshot.fallback_reason)}
                    color={liveSnapshot.fallback_reason === 'prod_name_mismatch' ? 'error' : 'warning'}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Tooltip>
              )}
              <Typography variant="caption" color="text.secondary">
                {liveSnapshot.counts.subscriptions_active} active sub(s) · {liveSnapshot.counts.contracts_total} contract(s) · {liveSnapshot.counts.submissions_recent} recent submission(s)
              </Typography>
            </Stack>
            {liveStateExpanded && (
              <Box sx={{ mt: 1 }} onClick={e => e.stopPropagation()}>
                <SubscriptionOverlayLegend />
              </Box>
            )}
          </Box>
          <Collapse in={liveStateExpanded} unmountOnExit>
          {liveSnapshot.subscriptions.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No subscriptions currently on this Heartbeat account.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { textAlign: 'left', py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>After submission</TableCell>
                    <TableCell>Service Line</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Recurring</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {liveSnapshot.subscriptions.map(s => {
                    const r = resolutionById.get(s.id)
                    const style = overlayStyle(r?.status ?? 'no_predecessor')
                    return (
                      <TableRow key={s.id} hover sx={{ bgcolor: style.rowBg }}>
                        <TableCell sx={{ borderLeft: `4px solid ${style.borderColor}` }}>
                          <Tooltip title={r?.match_reason ?? '—'} placement="right">
                            <Chip
                              size="small"
                              label={style.label}
                              sx={{
                                bgcolor: style.chipBg,
                                color: style.chipFg,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{s.service_line?.code ?? s.subscription_item?.service_line ?? '—'}</TableCell>
                        <TableCell>{s.subscription_item?.display_name ?? s.name ?? '—'}</TableCell>
                        <TableCell>
                          {s.status && (
                            <Typography variant="body2" sx={{ color: renderColorByString(s.status), fontWeight: 500 }}>
                              {s.status}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatCurrency(typeof s.recurring_amount === 'string' ? parseFloat(s.recurring_amount) : s.recurring_amount)}</TableCell>
                        <TableCell>{formatCurrency(typeof s.total === 'string' ? parseFloat(s.total) : s.total)}</TableCell>
                        <TableCell>{formatDate(s.start_date)}</TableCell>
                        <TableCell>{formatDate(s.end_date)}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </Collapse>
        </Paper>
      )}

      {currentHB && currentHB.available === false && currentHB.reason !== 'no_account_id' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn't fetch live Heartbeat state ({currentHB.reason.replace('_', ' ')}). The "Would push" preview above is still accurate.
        </Alert>
      )}

      {/* ── Visual separator between live read-only state and push history ── */}
      <Divider sx={{ my: 3 }}>
        <Chip
          label="PUSH HISTORY"
          size="small"
          sx={{
            fontFamily: 'monospace',
            fontSize: 11,
            letterSpacing: '0.08em',
            color: 'text.secondary',
            bgcolor: 'action.hover',
          }}
        />
      </Divider>

      {/* Heartbeat push timeline — every push attempt against this
          deal, newest first. Read-only. Section hides itself entirely
          when the deal has no HB linkage AND no push history. */}
      <HeartbeatPushTimeline
        PK={PK}
        SK={SK}
        dealInHeartbeat={Boolean(currentHB?.available)}
      />
    </Box>
  )
}

export default DealHeartbeatPreview
